import { useCallback, useMemo } from "react";

import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Header,
  Icon,
  List,
  Loader,
  Message,
  SemanticICONS,
} from "semantic-ui-react";

import { initUseQuery } from "simplydo/core/query";
import { openDownloadLink } from "simplydo/core/storage";

import api from "api";
import { useAppSelector } from "store";

import Login from "components/entry/Login";

import { FileIconMap } from "./Notifications/Downloads";

const formatFileSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
};

export const Download = () => {
  const user = useAppSelector((state) => state.user);
  const [searchParams] = useSearchParams();

  const paramDict = useMemo(() => {
    const forType = searchParams.get("forType") || undefined;
    const forId = searchParams.get("forId") || undefined;
    const identifier = searchParams.get("identifier") || undefined;
    const entityType = searchParams.get("entityType") || undefined;
    const entityId = searchParams.get("entityId") || undefined;

    return { forType, forId, identifier, entityType, entityId };
  }, [searchParams]);

  const fileDetailQuery = useQuery(
    initUseQuery(api.uploads.getDownloadDetails, [paramDict], {
      isEnabled: !!paramDict.forType && !!paramDict.forId,
    }),
  );

  const fileType = fileDetailQuery.data?.fileType;
  const fileIcon =
    fileType ?
      (FileIconMap[fileType as keyof typeof FileIconMap] ?? "file outline")
    : "file outline";

  const checkFileDownload = useCallback(() => {
    api.uploads.getFileDownloadCookies(
      paramDict,
      ({ url }) => {
        openDownloadLink(url, fileDetailQuery.data?.fileName ?? "");
      },
      (error) => {
        if (error.status === 404) {
          toast.error("This file no longer exists or has been removed.");
          return;
        }
        toast.error("Could not download file, please try again later.");
      },
    );
  }, [paramDict, fileDetailQuery.data]);

  if (!user) {
    return (
      <div>
        <h2>File download</h2>
        <Message style={{ marginTop: 0 }} info>
          To download this file, please log in or sign up.
        </Message>
        <Login embedded />
      </div>
    );
  }

  if (!paramDict.forType || !paramDict.forId) {
    return (
      <div>
        <h2>File download</h2>
        <Message warning>
          <Message.Header>Something went wrong</Message.Header>
          The link to this file is invalid or the file does not exist, please
          try again. If the problem persists, please contact support.
        </Message>
        <Button primary as={Link} to="/">
          Go to homepage
        </Button>
      </div>
    );
  }

  if (fileDetailQuery.error) {
    return (
      <div>
        <h2>File download</h2>
        <Message style={{ marginTop: 0 }} error>
          This file is not available for download. The file either does not
          exist or you are not authorised to access it.
        </Message>
        <Button primary as={Link} to="/">
          Go to homepage
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Header as="h2">
        File download
        <Header.Subheader>
          You can download the file by clicking the download button below.
        </Header.Subheader>
      </Header>

      {fileDetailQuery.isLoading ?
        <div
          style={{
            minHeight: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader inline="centered" active />
        </div>
      : <div>
          <Message>
            <Message.Header>
              <Icon name={fileIcon as SemanticICONS} /> File details
            </Message.Header>
            <List divided relaxed>
              <List.Item>
                <List.Header>File name:</List.Header>
                <List.Description>
                  {fileDetailQuery.data?.fileName}
                </List.Description>
              </List.Item>
              <List.Item>
                <List.Header>File size:</List.Header>
                <List.Description>
                  {formatFileSize(fileDetailQuery.data?.fileSize ?? 0)}
                </List.Description>
              </List.Item>
              <List.Item>
                <List.Header>File type:</List.Header>
                <List.Description>{fileType}</List.Description>
              </List.Item>
            </List>
          </Message>

          <div style={{ float: "right" }}>
            <Button
              onClick={() => {
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => {
                    toast.success("Link copied to clipboard");
                  })
                  .catch(() => {
                    toast.error("Could not copy link to clipboard");
                  });
              }}
            >
              <Icon name="copy" /> Copy link
            </Button>
            <Button
              onClick={() => checkFileDownload()}
              primary
              content="Download"
            >
              <Icon name="download" /> Download
            </Button>
          </div>
        </div>
      }
    </div>
  );
};
